import { CatalogItem } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useMemo } from 'react';
import { z } from 'zod';

import { useToolFundCatalogItemCategoryQuery } from '../../../hooks/react-query/useToolFundCatalogItemCategoryQuery';
import {
  useCatalogItemCreateMutation,
  useCatalogItemUpdateMutation,
} from '../../../hooks/react-query/useToolFundCatalogQuery';
import { useToolTradeMappingSyncMutation } from '../../../hooks/react-query/useToolTradeMappingQuery';
import { useBatchGetTradeTypes } from '../../../hooks/react-query/useTradeTypeQuery';

/**
 * The Add Tool / Edit Tool form's rules and save, shared by desktop's `CatalogItemFormDialog`
 * and the phone's `CatalogItemFormPhone`. Moved from `CatalogItemFormDialog` unchanged.
 */
export const catalogItemSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  brand: z.string().optional().default(''),
  description: z.string().optional().default(''),
  categoryId: z.coerce.number().min(1, 'Category is required'),
  cost: z.coerce.number().min(0, 'Price must be 0 or more'),
  externalLink: z.string().optional().default(''),
  isActive: z.boolean().default(true),
  tradeTypeIds: z.array(z.number()).default([]),
});

export type CatalogItemSchema = z.infer<typeof catalogItemSchema>;

export const getCatalogItemFormDefaultValues = (item: CatalogItem | null): CatalogItemSchema => ({
  name: item?.name ?? '',
  brand: item?.brand ?? '',
  description: item?.description ?? '',
  categoryId: item?.categoryId ?? 0,
  cost: item?.cost ?? 0,
  externalLink: item?.externalLink ?? '',
  isActive: item ? item.isActive : true,
  tradeTypeIds: item?.tradeTypeIdList
    ? item.tradeTypeIdList.split(',').filter(Boolean).map(Number)
    : [],
});

export const isCatalogItemEdit = (item: CatalogItem | null) => !!item && item.id > 0;

/** The category choices and the trades a tool can be tagged with. */
export const useCatalogItemFormOptions = () => {
  const { data: categories } = useToolFundCatalogItemCategoryQuery({ filter: { pageNumber: -1 } });
  const categoryOptions = useMemo(() => {
    return (
      categories?.results?.map((category) => ({
        label: category.name,
        value: category.id.toString(),
      })) ?? []
    );
  }, [categories]);

  const { data: tradeTypes } = useBatchGetTradeTypes({
    select(data) {
      return data.results || [];
    },
  });

  return { categoryOptions, tradeTypes };
};

/**
 * Saves the tool (update by field mask, or create), then its trades, then toasts. `onSaved` runs
 * where desktop closed its dialog.
 */
export const useSaveCatalogItem = ({
  item,
  onSaved,
}: {
  item: CatalogItem | null;
  onSaved: () => void;
}) => {
  const isEdit = isCatalogItemEdit(item);
  const createMutation = useCatalogItemCreateMutation();
  const updateMutation = useCatalogItemUpdateMutation();
  const syncTradesMutation = useToolTradeMappingSyncMutation();

  const isSaving =
    createMutation.isPending || updateMutation.isPending || syncTradesMutation.isPending;

  const save = async (values: CatalogItemSchema) => {
    try {
      let itemId = item?.id ?? 0;

      if (isEdit) {
        await updateMutation.mutateAsync(
          CatalogItem.create({
            id: itemId,
            name: values.name,
            brand: values.brand,
            description: values.description,
            categoryId: values.categoryId,
            cost: values.cost,
            externalLink: values.externalLink,
            isActive: values.isActive,
            fieldMask: [
              'Name',
              'Brand',
              'Description',
              'CategoryId',
              'Cost',
              'ExternalLink',
              'IsActive',
            ],
          }),
        );
      } else {
        const created = await createMutation.mutateAsync(
          CatalogItem.create({
            name: values.name,
            brand: values.brand,
            description: values.description,
            categoryId: values.categoryId,
            cost: values.cost,
            externalLink: values.externalLink,
            isActive: true,
            isToolFundEligible: true,
          }),
        );
        itemId = created.id;
      }

      await syncTradesMutation.mutateAsync({
        catalogItemId: itemId,
        tradeTypeIds: values.tradeTypeIds,
      });

      toast({
        title: 'Success',
        description: isEdit ? 'Tool updated' : 'Tool added to catalog',
      });
      onSaved();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save tool',
        variant: 'destructive',
      });
    }
  };

  return { save, isSaving, isEdit };
};
